// v0.3.0: WebSocket protocol shared by Worker/DO and the browser client.
// v0.8.0: side-view world — sit state, personal desks, richer avatar config.

export type Dir = 'up' | 'down' | 'left' | 'right';

// v0.15.0: Ottsy — everyone is an otter. Fixed palettes; designs are indexes 0-7.
export const EXPRS = ['normal', 'happy', 'laugh', 'heart', 'shock', 'angry', 'wink', 'cool'] as const;
export type Expr = (typeof EXPRS)[number];
export const FUR_COLORS = ['#a9744f', '#7a5230', '#c9a27a', '#6e6a63', '#4b4b52', '#d99a6c', '#8fb3c9', '#b48fd1'];
export const CLOTH_COLORS = ['#f5f5f7', '#e04848', '#f0932b', '#f6d743', '#6cc26a', '#3f6fd8', '#9a6ee0', '#2f3548'];
export const EYE_STYLES = ['round', 'dot', 'sparkle', 'sleepy', 'happy', 'wink', 'cool', 'star'] as const;
export type EyeStyle = (typeof EYE_STYLES)[number];
export const HAT_STYLES = ['none', 'cap', 'beanie', 'flower', 'bow', 'headphones', 'crown', 'leaf', 'sunhat'] as const; // v1.8.0: + sunhat
export type HatStyle = (typeof HAT_STYLES)[number];
export const SHIRT_STYLES = ['none', 'tee', 'hoodie', 'scarf', 'overalls', 'raincoat', 'sweater', 'vest', 'apron'] as const; // v1.8.0: + apron
export const PANTS_STYLES = ['none', 'shorts', 'skirt', 'jeans', 'joggers', 'checks', 'cargo', 'tutu'] as const;
/** The starter otter — natural brown fur, round eyes, a red scarf. */
export const DEFAULT_AVATAR = { color: '#e04848', pants: '#3f6fd8', expr: 'normal', shirtStyle: 3, pantsStyle: 0, fur: '#a9744f', eyes: 'round', hat: 'none', hatColor: '#f6d743' } as const;

export interface AvatarConfig {
  /** top colour */
  color: string;
  initials: string;
  /** bottom colour */
  pants?: string;
  expr?: Expr;
  shirtStyle?: number;
  pantsStyle?: number;
  fur?: string;
  eyes?: EyeStyle;
  hat?: HatStyle;
  hatColor?: string;
}

// v0.7.0: emotes. v0.9.0: F1-F6 expressions drawn on the face; 'jump' is Space.
export const EMOTES = ['wave', 'dance', 'laugh', 'heart', 'shock', 'angry', 'jump'] as const;
export type EmoteKind = (typeof EMOTES)[number];
export const EMOTE_MS = 2200;
export const JUMP_MS = 650;

/** v0.16.0: held poses — hands up (↑ while still) or flat on the floor (↓ while still). */
export type Pose = 'none' | 'handsup' | 'flat';

export interface PlayerState {
  id: string;
  name: string;
  avatar: AvatarConfig;
  x: number;
  y: number;
  dir: Dir;
  /** v0.8.0: sitting on the seat at (x,y) */
  sit: boolean;
  pose?: Pose;
  /** Private zone name the player is inside, or null. */
  zone: string | null;
  /** Realtime SFU session id once the client has published tracks. */
  rtc: string | null;
  /** Names of tracks this player has published (e.g. "<id>-mic"). */
  tracks: string[];
  mic: boolean;
  cam: boolean;
  screen: boolean;
}

export interface ChatMsg {
  id: number;
  userId: string;
  name: string;
  body: string;
  ts: number;
}

/** v1.20.0: Gather-style "Nearby" chat: goes only to the people you can hear right now (same room, or close by), never stored */
export interface NearbyMsg { id: string; userId: string; name: string; body: string; ts: number; zone: string | null; to: string[] }
export const NEARBY_MAX_LEN = 500;

export interface MediaPeer {
  id: string;
  rtc: string;
  tracks: string[];
}

/** desk id -> owner */
export type DeskMap = Record<string, { userId: string; name: string }>;
/** v0.17.0: zone key (map name) -> display name override */
export type RoomNames = Record<string, string>;
export const ROOM_NAME_MAX = 32;
/** v0.18.0: customisable environment — 5 kinds x STYLE_COUNT styles each */
import type { Facing, PlaceKind, Placement } from './map';
export type { Facing, PlaceKind, Placement } from './map';
export const PLACE_KINDS: PlaceKind[] = ['table', 'chair', 'block', 'platform', 'decor'];

// ---------- v1.1.0: Slack-style chat ----------
export interface ChatFile { id: string; name: string; mime: string; size: number; url: string }
export interface ChatMessage {
  id: string; channelId: string; userId: string | null; kind: 'user' | 'system' | 'webhook';
  body: string; ts: number; editedAt: number | null; deleted: boolean;
  threadId: string | null; replyCount: number; lastReplyTs: number | null;
  meta: { mentions?: string[]; files?: ChatFile[]; name?: string; icon?: string };
  reactions: { emoji: string; users: string[] }[];
  pinned: boolean; saved: boolean;
}
export interface ChatChannel {
  id: string; name: string; kind: 'public' | 'private' | 'dm' | 'group'; topic: string; purpose: string; archived: boolean;
  members: string[]; unread: number; mentions: number; lastTs: number; createdBy: string | null;
  /** v1.2.0: I muted this channel (no notifications / bold) */
  muted: boolean;
}
export type ChatEvent =
  | { type: 'message'; message: ChatMessage; edit?: boolean }
  | { type: 'delete'; channelId: string; messageId: string; threadId: string | null }
  | { type: 'reaction'; channelId: string; messageId: string; threadId: string | null; emoji: string; userId: string; on: boolean }
  | { type: 'pin'; channelId: string; messageId: string; threadId: string | null; on: boolean }
  | { type: 'channel'; channel: ChatChannel }
  | { type: 'membership'; channelId: string; userId: string; joined: boolean }
  | { type: 'typing'; channelId: string; userId: string; name: string }
  | { type: 'status'; userId: string; status: UserStatus };

/** v1.3.0: what someone set as their status */
export interface UserStatus { text: string; emoji: string; dndUntil: number }
export const STYLE_COUNT = 50;

// client -> server
export type ClientMsg =
  | { t: 'move'; dir: Dir; jump?: boolean; seq?: number }
  | { t: 'sit'; on: boolean }
  | { t: 'pose'; pose: Pose }
  | { t: 'desk'; action: 'claim' | 'release' }
  | { t: 'rename'; zone: string; name: string }
  | { t: 'place'; kind: PlaceKind; style: number; x: number; y: number; face: Facing }
  | { t: 'typing'; channelId: string }
  | { t: 'unplace'; id: number }
  | { t: 'chat'; body: string }
  | { t: 'nearby'; body: string }
  /** v1.21.0: ask someone to come over to you / answer such an ask */
  | { t: 'summon'; to: string }
  | { t: 'summon-reply'; to: string; ok: boolean; reason?: 'busy' | 'timeout' }
  | { t: 'rtc'; sessionId: string | null; tracks: string[] }
  | { t: 'av'; mic: boolean; cam: boolean; screen: boolean }
  | { t: 'profile'; name?: string; avatar?: AvatarConfig }
  | { t: 'emote'; kind: EmoteKind }
  | { t: 'ping' };

// server -> client
export type ServerMsg =
  | { t: 'welcome'; you: string; players: PlayerState[]; chat: ChatMsg[]; desks: DeskMap; rooms: RoomNames; placements: Placement[] }
  | { t: 'rooms'; rooms: RoomNames }
  | { t: 'placements'; placements: Placement[] }
  | { t: 'chat-ev'; ev: ChatEvent }
  | { t: 'join'; player: PlayerState }
  | { t: 'leave'; id: string }
  | { t: 'pos'; id: string; x: number; y: number; dir: Dir; sit: boolean; zone: string | null; seq?: number }
  | { t: 'desks'; desks: DeskMap }
  | { t: 'pose'; id: string; pose: Pose }
  | { t: 'chat'; msg: ChatMsg }
  | { t: 'nearby'; msg: NearbyMsg }
  | { t: 'summon'; from: string; name: string }
  | { t: 'summon-reply'; from: string; name: string; ok: boolean; reason?: 'busy' | 'timeout' }
  | { t: 'profile'; id: string; name: string; avatar: AvatarConfig }
  | { t: 'emote'; id: string; kind: EmoteKind }
  | { t: 'av'; id: string; mic: boolean; cam: boolean; screen: boolean }
  | { t: 'rtc'; id: string; rtc: string | null; tracks: string[] }
  | { t: 'media'; peers: MediaPeer[] }
  | { t: 'error'; msg: string }
  | { t: 'pong' };

// Proximity rules (§3): connect at <= 4 tiles, disconnect at >= 6 (hysteresis).
export const MEDIA_CONNECT_DIST = 4;
export const MEDIA_DISCONNECT_DIST = 6;
export const MOVE_MIN_INTERVAL_MS = 110; // client cadence (~9 moves/sec)
/** v0.12.0: server accepts slightly faster than the client sends, so jitter never causes rejections. */
export const SERVER_MOVE_MIN_MS = 80;
/**
 * v0.19.1: server-side move budget is a token bucket, not a fixed gap — the client sends
 * one step per MOVE_MIN_INTERVAL_MS but network jitter can bunch two steps together, and
 * a rejected step used to silently desync the client (it then "rubber-banded" back).
 */
export const MOVE_BUCKET = 4;
export const MOVE_REFILL_MS = 100;
export const CHAT_HISTORY = 200;
export const CHAT_MAX_LEN = 1000;
export const TILE = 32;

const HEX = /^#[0-9a-fA-F]{6}$/;
const HAT_LIST: readonly string[] = HAT_STYLES;
const EYE_LIST: readonly string[] = EYE_STYLES;
/** Validate/merge an avatar config coming from a client. */
export function sanitizeAvatar(input: Partial<AvatarConfig> | undefined, base: AvatarConfig): AvatarConfig {
  const a = input ?? {};
  const idx = (v: unknown, fallback: number | undefined) => (typeof v === 'number' && Number.isInteger(v) && v >= 0 && v < 8 ? v : fallback);
  const out: AvatarConfig = {
    color: HEX.test(a.color ?? '') ? a.color! : base.color,
    initials: (String(a.initials ?? '').trim().slice(0, 2).toUpperCase()) || base.initials,
  };
  const pants = HEX.test(a.pants ?? '') ? a.pants : base.pants;
  const fur = HEX.test(a.fur ?? '') ? a.fur : base.fur;
  const hatColor = HEX.test(a.hatColor ?? '') ? a.hatColor : base.hatColor;
  const expr = (EXPRS as readonly string[]).includes(a.expr ?? '') ? a.expr : base.expr;
  const eyes = EYE_LIST.includes(a.eyes ?? '') ? a.eyes : base.eyes;
  const hat = HAT_LIST.includes(a.hat ?? '') ? a.hat : base.hat;
  const shirtStyle = idx(a.shirtStyle, base.shirtStyle);
  const pantsStyle = idx(a.pantsStyle, base.pantsStyle);
  if (pants) out.pants = pants;
  if (fur) out.fur = fur;
  if (hatColor) out.hatColor = hatColor;
  if (expr) out.expr = expr;
  if (eyes) out.eyes = eyes;
  if (hat) out.hat = hat;
  if (shirtStyle !== undefined) out.shirtStyle = shirtStyle;
  if (pantsStyle !== undefined) out.pantsStyle = pantsStyle;
  return out;
}
